import { ApolloClient, gql } from '@apollo/client/core';
import { CONNECTION_METADATA_FIELDS } from '../connection/Api';
import { Query } from '../../generated/graphql';
import * as GqlConnection from '../connection/GqlConnection';
import * as TicketLayout from './TicketLayout';
import { VPResult } from '@/api/VPResult';

const TICKET_LAYOUT_FIELDS = gql`
  fragment TicketLayoutFields on TicketLayout {
    id
    name
    fglString
    layout
    default
  }
`;

const TICKET_LAYOUT_QUERY = gql`
  query TicketLayout($id: Int!) {
    app {
      ticketLayout(id: $id) {
        ...TicketLayoutFields
      }
    }
  }
  ${TICKET_LAYOUT_FIELDS}
`;

const TICKET_LAYOUTS_QUERY = gql`
  query Layouts($after: String, $before: String, $first: Int) {
    app {
      ticketLayouts(after: $after, before: $before, first: $first) {
        edges {
          cursor
          node {
            ...TicketLayoutFields
          }
        }
        pageInfo {
          ...ConnectionMetadataFields
        }
      }
    }
  }
  ${CONNECTION_METADATA_FIELDS}
  ${TICKET_LAYOUT_FIELDS}
`;

export const fetchLayouts = async (client: ApolloClient<Query>, { after, first }: { after?: string; first?: number }) => {
  const { data } = await client.query({
    query: TICKET_LAYOUTS_QUERY,
    variables: {
      after,
      first,
    },
  });

  return GqlConnection.fromGql(data.app.ticketLayouts, TicketLayout.fromGQL);
};

export const fetchLayout = async (client: ApolloClient<Query>, id: number): Promise<TicketLayout.TicketLayout | undefined> => {
  const { data } = await client.query({
    query: TICKET_LAYOUT_QUERY,
    variables: { id },
  });

  if (!data.app.ticketLayout) {
    return undefined;
  }

  return TicketLayout.fromGQL(data.app.ticketLayout);
};

export type TicketLayoutInput = {
  id?: string;
  name: string;
  fglString: string;
  layout?: unknown;
  default?: boolean;
};

const SAVE_TICKET_LAYOUT_MUTATION = gql`
  mutation SaveTicketLayout($ticketLayout: TicketLayoutAttributes!) {
    app {
      saveTicketLayout(input: { ticketLayout: $ticketLayout }) {
        ticketLayout {
          ...TicketLayoutFields
        }
        errors
      }
    }
  }
  ${TICKET_LAYOUT_FIELDS}
`;

export const saveTicketLayout = async (client: ApolloClient<Query>, layout: TicketLayoutInput): Promise<VPResult<TicketLayout.TicketLayout>> => {
  const { data } = await client.mutate({
    mutation: SAVE_TICKET_LAYOUT_MUTATION,
    variables: {
      ticketLayout: layout,
    },
  });
  client.cache.evict({ id: `TicketLayout:${layout.id}` });
  client.cache.gc();

  if (data.app.saveTicketLayout.errors) {
    return {
      success: false,
      errors: data.app.saveTicketLayout.errors,
    };
  }

  return {
    success: true,
    data: data.app.saveTicketLayout.ticketLayout && TicketLayout.fromGQL(data.app.saveTicketLayout.ticketLayout),
  };
};
